(function () {
  const initializedMaps = new WeakSet();

  function normalizeId(value) {
    if (value === null || value === undefined) return '';

    const match = String(value).match(/(\d+)$/);

    return match ? match[1] : String(value);
  }

  function escapeCss(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value));
    }

    return String(value)
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"');
  }

  function dispatchControlChange(control) {
    if (!control) return;

    control.dispatchEvent(
      new Event('input', {
        bubbles: true
      })
    );

    control.dispatchEvent(
      new Event('change', {
        bubbles: true
      })
    );
  }

  function getSection(mapElement) {
    return (
      mapElement.closest('[id^="shopify-section-"]') ||
      mapElement.closest('.shopify-section') ||
      document
    );
  }

  function getGallery(section) {
    return (
      section.querySelector('product-gallery') ||
      section.querySelector('.product-gallery') ||
      section.querySelector('.product-gallery__media-list')
    );
  }

  function getScroller(gallery) {
    if (!gallery) return null;

    if (
      gallery.matches(
        'media-carousel, .product-gallery__media-list'
      )
    ) {
      return gallery;
    }

    return (
      gallery.querySelector('media-carousel') ||
      gallery.querySelector('.product-gallery__media-list') ||
      gallery
    );
  }

  function isVariantControl(target) {
    if (!(target instanceof Element)) return false;

    return Boolean(
      target.closest(
        [
          'variant-picker',
          'variant-selects',
          'product-variants',
          '.variant-picker',
          '.product-info__variant-picker',
          '.product-form__input',
          'input[name^="options"]',
          'select[name^="options"]',
          'input[name^="option"]',
          'select[name^="option"]',
          'input[name="id"]',
          'select[name="id"]'
        ].join(',')
      )
    );
  }

  function isGalleryInteraction(target) {
    if (!(target instanceof Element)) return false;

    return Boolean(
      target.closest(
        [
          'product-gallery',
          'media-carousel',
          '.product-gallery',
          '.product-gallery__media-list',
          '.product-gallery__media',
          '.product-gallery__thumbnail',
          '.zl-mbu-prev',
          '.zl-mbu-next'
        ].join(',')
      )
    );
  }

  function getCurrentVariantId(section) {
    const variantInput = section.querySelector(
      'input[name="id"], select[name="id"]'
    );

    return variantInput
      ? String(variantInput.value)
      : '';
  }

  function selectVariantOption(
    section,
    optionName,
    optionIndex,
    optionValue
  ) {
    const value = String(optionValue);
    const escapedName = escapeCss(optionName || '');

    const select =
      section.querySelector(
        `select[name="options[${escapedName}]"]`
      ) ||
      section.querySelector(
        `select[name="option${optionIndex + 1}"]`
      ) ||
      section.querySelector(
        `select[name="option-${optionIndex + 1}"]`
      );

    if (select) {
      if (select.value !== value) {
        select.value = value;
        dispatchControlChange(select);
      }

      return true;
    }

    const radios = section.querySelectorAll(
      'input[type="radio"]'
    );

    for (const radio of radios) {
      if (String(radio.value) !== value) continue;

      const radioName =
        radio.getAttribute('name') || '';

      const matchesOption =
        radioName === `options[${optionName}]` ||
        radioName === `option${optionIndex + 1}` ||
        radioName === `option-${optionIndex + 1}` ||
        (
          optionName &&
          radioName.includes(optionName)
        );

      if (!matchesOption) continue;

      if (!radio.checked) {
        radio.checked = true;
        dispatchControlChange(radio);
      }

      return true;
    }

    return false;
  }

  function init(mapElement) {
    if (
      !mapElement ||
      initializedMaps.has(mapElement)
    ) {
      return;
    }

    initializedMaps.add(mapElement);

    const section = getSection(mapElement);
    const gallery = getGallery(section);
    const scroller = getScroller(gallery);

    if (!gallery || !scroller) return;

    let config;

    try {
      config = JSON.parse(
        mapElement.textContent || '{}'
      );
    } catch (error) {
      return;
    }

    const optionNames = Array.isArray(config.options)
      ? config.options
      : [];

    const variants = Array.isArray(config.variants)
      ? config.variants.filter(
          variant =>
            variant &&
            variant.id &&
            variant.media_id
        )
      : [];

    if (!variants.length) return;

    const variantByMediaId = new Map();

    variants.forEach(variant => {
      const mediaId = normalizeId(
        variant.media_id
      );

      if (
        mediaId &&
        !variantByMediaId.has(mediaId)
      ) {
        variantByMediaId.set(
          mediaId,
          variant
        );
      }
    });

    if (!variantByMediaId.size) return;

    let mediaElements = Array.from(
      gallery.querySelectorAll(
        '.product-gallery__media[data-media-id]'
      )
    );

    let mediaPositions = [];
    let positionsDirty = true;

    let syncing = false;
    let userGalleryAction = false;
    let mutedUntil = 0;

    let settleTimer = null;
    let animationFrame = null;
    let releaseTimer = null;

    function refreshMediaElements() {
      mediaElements = Array.from(
        gallery.querySelectorAll(
          '.product-gallery__media[data-media-id]'
        )
      );

      positionsDirty = true;
    }

    function rebuildMediaPositions() {
      const scrollerRect =
        scroller.getBoundingClientRect();

      const scrollLeft = scroller.scrollLeft;

      mediaPositions = mediaElements
        .map(element => {
          const rect =
            element.getBoundingClientRect();

          if (
            rect.width <= 1 ||
            rect.height <= 1
          ) {
            return null;
          }

          const center =
            rect.left -
            scrollerRect.left +
            scrollLeft +
            rect.width / 2;

          return {
            id: normalizeId(
              element.getAttribute(
                'data-media-id'
              )
            ),
            center
          };
        })
        .filter(
          item => item && item.id
        )
        .sort(
          (first, second) =>
            first.center - second.center
        );

      positionsDirty = false;
    }

    function getStateMediaId() {
      const activeMedia =
        gallery.querySelector(
          '.product-gallery__media.is-selected[data-media-id]'
        ) ||
        gallery.querySelector(
          '.product-gallery__media[aria-current="true"][data-media-id]'
        ) ||
        gallery.querySelector(
          '.product-gallery__media[aria-hidden="false"][data-media-id]'
        );

      return activeMedia
        ? normalizeId(
            activeMedia.getAttribute(
              'data-media-id'
            )
          )
        : '';
    }

    function getNearestMediaId() {
      const isScrollable =
        scroller.scrollWidth >
        scroller.clientWidth + 2;

      if (!isScrollable) {
        return getStateMediaId();
      }

      if (positionsDirty) {
        rebuildMediaPositions();
      }

      if (!mediaPositions.length) {
        return getStateMediaId();
      }

      const viewportCenter =
        scroller.scrollLeft +
        scroller.clientWidth / 2;

      let low = 0;
      let high = mediaPositions.length - 1;

      while (low < high) {
        const middle = Math.floor(
          (low + high) / 2
        );

        if (
          mediaPositions[middle].center <
          viewportCenter
        ) {
          low = middle + 1;
        } else {
          high = middle;
        }
      }

      const current = mediaPositions[low];
      const previous =
        low > 0
          ? mediaPositions[low - 1]
          : null;

      if (!previous) {
        return current.id;
      }

      const currentDistance = Math.abs(
        current.center - viewportCenter
      );

      const previousDistance = Math.abs(
        previous.center - viewportCenter
      );

      return previousDistance <= currentDistance
        ? previous.id
        : current.id;
    }

    function clearScheduledSync() {
      if (settleTimer) {
        clearTimeout(settleTimer);
        settleTimer = null;
      }

      if (animationFrame) {
        cancelAnimationFrame(
          animationFrame
        );

        animationFrame = null;
      }
    }

    function scheduleSync(delay) {
      if (
        syncing ||
        !userGalleryAction ||
        Date.now() < mutedUntil
      ) {
        return;
      }

      if (settleTimer) {
        clearTimeout(settleTimer);
      }

      settleTimer = setTimeout(() => {
        settleTimer = null;

        if (animationFrame) {
          cancelAnimationFrame(
            animationFrame
          );
        }

        animationFrame =
          requestAnimationFrame(() => {
            animationFrame = null;
            syncVariantByMedia();
          });
      }, delay);
    }

    function markGalleryAction(delay) {
      if (
        syncing ||
        Date.now() < mutedUntil
      ) {
        return;
      }

      userGalleryAction = true;
      scheduleSync(delay);
    }

    function updateVariantIdInputs(
      variantId
    ) {
      const idInputs =
        section.querySelectorAll(
          'input[name="id"], select[name="id"]'
        );

      idInputs.forEach(input => {
        if (
          String(input.value) ===
          String(variantId)
        ) {
          return;
        }

        input.value = variantId;
        dispatchControlChange(input);
      });
    }

    function updateVariantUrl(variantId) {
      const url = new URL(
        window.location.href
      );

      if (
        url.searchParams.get('variant') ===
        String(variantId)
      ) {
        return;
      }

      url.searchParams.set(
        'variant',
        variantId
      );

      window.history.replaceState(
        window.history.state,
        '',
        url.toString()
      );
    }

    function releaseSync() {
      if (releaseTimer) {
        clearTimeout(releaseTimer);
      }

      releaseTimer = setTimeout(() => {
        syncing = false;
        releaseTimer = null;
      }, 400);
    }

    function syncVariantByMedia() {
      if (
        syncing ||
        !userGalleryAction ||
        Date.now() < mutedUntil
      ) {
        return;
      }

      const mediaId =
        getNearestMediaId();

      if (!mediaId) {
        userGalleryAction = false;
        return;
      }

      const variant =
        variantByMediaId.get(mediaId);

      if (!variant) {
        userGalleryAction = false;
        return;
      }

      if (
        String(variant.id) ===
        getCurrentVariantId(section)
      ) {
        userGalleryAction = false;
        return;
      }

      syncing = true;
      userGalleryAction = false;
      mutedUntil = Date.now() + 700;

      const variantOptions =
        Array.isArray(variant.options)
          ? variant.options
          : [];

      variantOptions.forEach(
        (optionValue, optionIndex) => {
          selectVariantOption(
            section,
            optionNames[optionIndex],
            optionIndex,
            optionValue
          );
        }
      );

      updateVariantIdInputs(
        variant.id
      );

      updateVariantUrl(
        variant.id
      );

      releaseSync();
    }

    section.addEventListener(
      'pointerdown',
      event => {
        if (
          !isVariantControl(event.target)
        ) {
          return;
        }

        userGalleryAction = false;
        mutedUntil = Date.now() + 800;

        clearScheduledSync();
      },
      true
    );

    section.addEventListener(
      'change',
      event => {
        if (
          !isVariantControl(event.target)
        ) {
          return;
        }

        userGalleryAction = false;
        mutedUntil = Date.now() + 800;

        clearScheduledSync();
      },
      true
    );

    gallery.addEventListener(
      'pointerdown',
      event => {
        if (
          isGalleryInteraction(
            event.target
          )
        ) {
          markGalleryAction(180);
        }
      },
      {
        passive: true
      }
    );

    gallery.addEventListener(
      'pointerup',
      event => {
        if (
          isGalleryInteraction(
            event.target
          )
        ) {
          markGalleryAction(100);
        }
      },
      {
        passive: true
      }
    );

    gallery.addEventListener(
      'click',
      event => {
        if (
          isGalleryInteraction(
            event.target
          )
        ) {
          markGalleryAction(100);
        }
      },
      {
        passive: true
      }
    );

    gallery.addEventListener(
      'wheel',
      () => {
        markGalleryAction(140);
      },
      {
        passive: true
      }
    );

    scroller.addEventListener(
      'scroll',
      () => {
        if (!userGalleryAction) return;

        scheduleSync(120);
      },
      {
        passive: true
      }
    );

    gallery.addEventListener(
      'load',
      () => {
        positionsDirty = true;
      },
      true
    );

    const mutationObserver =
      new MutationObserver(mutations => {
        let mediaStructureChanged = false;
        let activeStateChanged = false;

        for (const mutation of mutations) {
          if (
            mutation.type === 'childList'
          ) {
            mediaStructureChanged = true;
            break;
          }

          if (
            mutation.type === 'attributes'
          ) {
            activeStateChanged = true;
          }
        }

        if (mediaStructureChanged) {
          refreshMediaElements();
        }

        if (
          activeStateChanged &&
          userGalleryAction
        ) {
          scheduleSync(60);
        }
      });

    mutationObserver.observe(gallery, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [
        'class',
        'aria-hidden',
        'aria-current'
      ]
    });

    if (
      typeof ResizeObserver !==
      'undefined'
    ) {
      const resizeObserver =
        new ResizeObserver(() => {
          positionsDirty = true;
        });

      resizeObserver.observe(scroller);
    } else {
      window.addEventListener(
        'resize',
        () => {
          positionsDirty = true;
        },
        {
          passive: true
        }
      );
    }
  }

  function initAll(root) {
    const scope = root || document;

    if (
      scope instanceof Element &&
      scope.matches(
        '[id^="ZlVariantMediaMap-"]'
      )
    ) {
      init(scope);
    }

    scope
      .querySelectorAll(
        '[id^="ZlVariantMediaMap-"]'
      )
      .forEach(init);
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initAll(document);
      },
      {
        once: true
      }
    );
  } else {
    initAll(document);
  }

  document.addEventListener(
    'shopify:section:load',
    event => {
      initAll(event.target);
    }
  );
})();